import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'App';
import { useState } from 'react';
import { ticketsService } from 'views/services/ticketsService';
import { useAdminsStore } from 'views/store/useAdminsStore';
import { FaCopy } from 'react-icons/fa';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';

type AssignAdminDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  ticketId: string;
};

export function AssignAdminDialog({
  open,
  setOpen,
  ticketId,
}: AssignAdminDialogProps) {
  const { admins } = useAdminsStore();
  const [adminId, setAdminId] = useState('');
  const { canRead } = usePermissions();

  const handleConfirm = async () => {
    const result = await ticketsService.assignTicketToAdmin(ticketId, adminId);

    if (result) {
      setOpen(false);
    }
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast({
      title: 'Success',
      description: `Admin id - ${id} was been successfully copied!`,
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 z-50 backdrop-blur-sm" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-navy-800 p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold text-center mb-4 dark:text-white">
            Assign Ticket #{ticketId}
          </Dialog.Title>

          <div className="max-h-[300px] overflow-y-auto">
            {canRead(PERMISSION_RESOURCES.ADMINS) ? (
              admins?.map(admin => {
                const { id, login, name, role } = admin;
                return (
                  <div
                    key={id}
                    className="flex justify-between items-center gap-2 px-4 py-2 rounded-md dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 cursor-copy transition-colors"
                    onClick={() => handleCopyId(id)}
                  >
                    <div className="flex flex-col">
                      <p className="font-medium">{name ?? login}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-200">
                        {role}
                      </p>
                    </div>
                    <FaCopy />
                  </div>
                );
              })
            ) : (
              <div className="text-center py-4 text-gray-500">
                <p>No permission to view admins list</p>
                <p className="text-sm">Enter admin ID manually</p>
              </div>
            )}
          </div>

          <p className="mb-3 text-sm text-gray-700 text-center">
            Enter the admin ID you want to assign this ticket to:
          </p>

          <input
            type="text"
            placeholder="Admin ID"
            value={adminId}
            onChange={e => setAdminId(e.target.value)}
            className="w-full px-3 py-2 border rounded mb-4"
          />

          <div className="flex justify-end gap-2">
            <Dialog.Close asChild>
              <button className="px-4 py-2 text-sm border text-white rounded bg-red-500 hover:bg-red-400">
                Cancel
              </button>
            </Dialog.Close>
            <button
              onClick={handleConfirm}
              disabled={!adminId}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Confirm
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
